if (!customElements.get('fullscreen-video')) {
  class FullscreenVideo extends HTMLElement {
    constructor() {
      super();
      this._videoObserver = null;
      this._soundBtn = null;
      this._playPauseBtn = null;
      this._playIcon = null;
      this._pauseIcon = null;
      this._gestureUnlockHandler = null;
      this._onFirstScroll = null;
      this._onExternalPlay = null;
      this._audioUnlocked = false;
      this._isInView = false;
      this._userToggledSound = false;
      this._scrolled = false;
      this._vol = 0.25;
    }

    connectedCallback() {
      this._playPauseBtn = this.querySelector('.c-fullscreen-video__play-pause');
      this._playIcon = this.querySelector('.c-fullscreen-video__play-icon');
      this._pauseIcon = this.querySelector('.c-fullscreen-video__pause-icon');
      this._soundBtn = this.querySelector('[data-sound-toggle]');

      this._init();
    }

    disconnectedCallback() {
      this._teardown();
    }

    _getActiveVideo() {
      const videos = this.querySelectorAll('video');
      for (const video of videos) {
        if (video.offsetParent !== null) return video;
      }
      return null;
    }

    _setVolume(value) {
      const video = this._getActiveVideo();
      if (video) video.volume = value;
    }

    _init() {
      this._onExternalPlay = (e) => {
        if (e.detail.source === this) return;
        this._pauseAndSilence();
      };
      document.addEventListener('video-section:play', this._onExternalPlay);

      this._initVideo();
    }

    _initVideo() {
      const videos = this.querySelectorAll('video');
      if (!videos.length) return;

      const autoSound = this.dataset.autoplay === 'true' && this.dataset.muted === 'false';
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      this._vol = isIOS ? 0.1 : parseInt(this.dataset.volume || '20', 10) / 100;
      const vol = this._vol;

      videos.forEach(video => {
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');
        video.muted = true;
        video.pause();

        video.addEventListener('play', () => {
          if (video.offsetParent !== null) this._updatePlayUI(true);
        });
        video.addEventListener('pause', () => {
          if (video.offsetParent !== null) this._updatePlayUI(false);
        });
      });

      this._updateSoundIcons(true);

      if (this._playPauseBtn) {
        this._playPauseBtn.addEventListener('click', () => this._togglePlay());
      }

      if (this._soundBtn) {
        this._soundBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this._userToggledSound = true;
          const video = this._getActiveVideo();
          if (!video) return;
          if (this._audioUnlocked) {
            const isSilent = video.volume === 0;
            this._setVolume(isSilent ? vol : 0);
            this._updateSoundIcons(!isSilent);
          } else {
            video.muted = !video.muted;
            this._updateSoundIcons(video.muted);
          }
        });
      }

      this._onFirstScroll = () => {
        this._scrolled = true;
        window.removeEventListener('scroll', this._onFirstScroll);

        // If section is already in viewport, start playback now
        if (this._isInView) {
          const video = this._getActiveVideo();
          if (video) {
            document.dispatchEvent(new CustomEvent('video-section:play', { detail: { source: this } }));
            video.play().catch(() => {});
            if (autoSound && !this._userToggledSound && this._audioUnlocked) {
              this._setVolume(vol);
              this._updateSoundIcons(false);
            }
          }
        }
      };
      window.addEventListener('scroll', this._onFirstScroll, { passive: true });

      // Gesture unlock: on first user gesture, unlock audio silently.
      // We play() with muted=false + volume=0 so iOS grants audio permission.
      // We restore the previous play state afterwards — observer controls playback.
      this._gestureUnlockHandler = () => {
        if (!autoSound || this._audioUnlocked || this._userToggledSound) return;
        const video = this._getActiveVideo();
        if (!video) return;

        const wasPaused = video.paused;
        video.muted = false;
        video.volume = 0;
        const playPromise = video.play();
        if (playPromise !== undefined) {
          playPromise.then(() => {
            this._audioUnlocked = true;
            // Restore previous state — don't start video, observer handles that
            if (wasPaused) video.pause();
          }).catch(() => {
            video.muted = true;
          });
        }
      };

      document.addEventListener('touchstart', this._gestureUnlockHandler, { passive: true });
      document.addEventListener('touchend', this._gestureUnlockHandler, { passive: true });
      document.addEventListener('click', this._gestureUnlockHandler, { passive: true });

      this._videoObserver = new IntersectionObserver((entries) => {
        const entry = entries[0];
        this._isInView = entry.isIntersecting;

        if (!this._scrolled) return;

        if (entry.isIntersecting) {
          document.dispatchEvent(new CustomEvent('video-section:play', { detail: { source: this } }));

          const video = this._getActiveVideo();
          if (!video) return;

          if (autoSound && !this._userToggledSound && this._audioUnlocked) {
            this._setVolume(vol);
            this._updateSoundIcons(false);
          }

          video.play().catch(() => {});
        } else {
          const video = this._getActiveVideo();
          if (!video) return;

          if (autoSound && !this._userToggledSound) {
            if (this._audioUnlocked) {
              this._setVolume(0);
            } else {
              video.muted = true;
            }
            this._updateSoundIcons(true);
          }

          video.pause();
        }
      }, { root: null, threshold: 0.3 });

      this._videoObserver.observe(this);
    }

    _pauseAndSilence() {
      const video = this._getActiveVideo();
      if (!video) return;
      video.pause();
      if (this._audioUnlocked) {
        this._setVolume(0);
      } else {
        video.muted = true;
      }
      this._updateSoundIcons(true);
      this._updatePlayUI(false);
    }

    _togglePlay() {
      const video = this._getActiveVideo();
      if (!video) return;
      if (video.paused) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    }

    _updatePlayUI(isPlaying) {
      if (!this._playIcon || !this._pauseIcon) return;
      this._playIcon.style.display = isPlaying ? 'none' : 'flex';
      this._pauseIcon.style.display = isPlaying ? 'flex' : 'none';
    }

    _updateSoundIcons(isSilent) {
      if (!this._soundBtn) return;
      const off = this._soundBtn.querySelector('.c-fullscreen-video__sound-icon--off');
      const on = this._soundBtn.querySelector('.c-fullscreen-video__sound-icon--on');
      if (off) off.style.display = isSilent ? 'flex' : 'none';
      if (on) on.style.display = isSilent ? 'none' : 'flex';
    }

    _teardown() {
      if (this._videoObserver) {
        this._videoObserver.disconnect();
        this._videoObserver = null;
      }
      if (this._gestureUnlockHandler) {
        document.removeEventListener('touchstart', this._gestureUnlockHandler);
        document.removeEventListener('touchend', this._gestureUnlockHandler);
        document.removeEventListener('click', this._gestureUnlockHandler);
        this._gestureUnlockHandler = null;
      }
      if (this._onFirstScroll) {
        window.removeEventListener('scroll', this._onFirstScroll);
        this._onFirstScroll = null;
      }
      if (this._onExternalPlay) {
        document.removeEventListener('video-section:play', this._onExternalPlay);
        this._onExternalPlay = null;
      }
    }
  }

  customElements.define('fullscreen-video', FullscreenVideo);
}
